import React, { useRef } from "react";

import styled from "styled-components/macro";

import usePaymentMethodState from "Payments/AddPaymentMethod/paymentMethodState";
import {
  useACHIsEditing,
  useAccountHolderName,
  useAccountNumber,
  useName,
  useReplaceExistingCard,
  useShouldRetokenize,
} from "Payments/AddPaymentMethod/paymentMethodStateHooks";

import {
  Info,
  Label,
  Number,
  SmallEditButton,
  SmallNumberInput,
  Stack,
} from "./styledComponents";

export default function AccountNumber() {
  const inputRef = useRef();
  const { last4, submitting, name, showUpdateUI } = usePaymentMethodState();
  const [shouldRetokenize, setShouldRetokenize] = useShouldRetokenize();
  const [, setReplaceExistingCard] = useReplaceExistingCard();

  const [isEditing, setIsEditing] = useACHIsEditing();
  const [accountNumber, setAccountNumber] = useAccountNumber();
  const { accountHolderName } = useAccountHolderName();
  const { setName } = useName();

  const handleEditBtnClick = () => {
    if (submitting || isEditing) return;
    setShouldRetokenize(true);
    setReplaceExistingCard(true);
    setIsEditing(true);

    // defaults to accountHolderName if name is empty and ensures name is sent to stax
    if (!name?.length && accountHolderName?.length) {
      setName(accountHolderName);
    }
  };

  const handleChange = (e) => {
    let value = e.target.value;
    let newValue = value.replace(/\s/g, "");
    let isACHFormatCompliant = /^([0-9]{0,17})$/g.test(newValue); // up to 17 alphanumeric characters

    if (!isACHFormatCompliant) return;

    setAccountNumber(newValue);

    if (!shouldRetokenize) {
      setShouldRetokenize(true);
    }
  };

  React.useEffect(() => {
    if (isEditing) return;

    if ((accountNumber?.length === 0 && last4?.length === 0) || showUpdateUI) {
      setIsEditing(true);
    }
  }, [accountNumber, last4, isEditing, setIsEditing, showUpdateUI]);

  return (
    <AccountNumberInfo onClick={handleEditBtnClick} $isEditing={isEditing}>
      <Stack>
        <Label>Account Number</Label>
        {isEditing ? (
          <SmallNumberInput
            ref={inputRef}
            value={accountNumber}
            type="text"
            inputMode="numeric"
            name="cardnumber"
            autoComplete="cc-number"
            autoFocus={true}
            tabIndex={1}
            onChange={handleChange}
            data-cy="stax-bank-account-number-input"
          />
        ) : (
          <Number>&bull; &bull; &bull; &bull; {last4}</Number>
        )}
      </Stack>

      {!isEditing && (
        <SmallEditButton
          key="edit_button"
          whileHover={{ scale: 1.1, color: "#278dc1" }}
          whileTap={{ scale: 0.95 }}
          exit={{
            opacity: 0,
            scale: 0.6,
            transition: { type: "tween" },
          }}
        >
          <div className="fas fa-pencil-alt" />
        </SmallEditButton>
      )}
    </AccountNumberInfo>
  );
}

const AccountNumberInfo = styled(Info)`
  width: 75%;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0);
  box-shadow: ${({ $isEditing }) =>
    `0 0 0 1px rgba(255, 255, 255, ${$isEditing ? 0.175 : 0}) inset`};

  &:hover {
    border: 2px solid rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
  }
  &:focus-within {
    border: 2px solid rgba(255, 255, 255, 1);
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
  }
`;
